import React, { useState, useContext, useEffect, useMemo } from 'react'
import { IntakeContext } from 'context/IntakeContext'
import { useFormDebounce } from 'hooks/useFormDebounce'
import { TSPDetails } from 'layouts/authenticated/pages/tsp/components/details'
import NewTsp from 'layouts/authenticated/pages/tsp/NewTsp'
import { Box } from '@mui/material'

import { Typography } from '@mui/material'
import tspImage from 'assets/images/tspdetails.png'

export const TSPPageIntro = () => (
  <Box p={2}>
    <Typography variant="h4" sx={{ mb: 2 }}>
      Enter Your TSP Fund Data
    </Typography>
    <Typography variant="body2" sx={{ mb: 2 }}>
      We recommend heading over to{' '}
      <a
        style={{ color: '#3498DB', textDecoration: 'underline' }}
        target="_blank"
        href="https://www.TSP.gov"
        rel="noreferrer"
      >
        www.TSP.gov
      </a>{' '}
      to sign in, then navigating to the{' '}
      <span style={{ fontWeight: 600, color: '#495057' }}>Investments</span>{' '}
      page to start copying over your fund information.
    </Typography>
    <img
      style={{
        alignSelf: 'center',
        objectFit: 'contain',
        width: '100%',
        height: '100%',
        marginTop: '24px',
        border: '1px solid #D2D6DA',
        borderRadius: 12,
      }}
      src={tspImage}
      alt="TSP Instructions"
    />
  </Box>
)

import { Grid } from '@mui/material'

import styled from 'styled-components'

// fundKeys.js
export const FUND_KEYS = [
  'tspGFund',
  'tspFFund',
  'tspCFund',
  'tspSFund',
  'tspIFund',
  'tspLFund',
]

export const L_FUND_KEYS = [
  'tspLIncome',
  'tspL2025',
  'tspL2030',
  'tspL2035',
  'tspL2040',
  'tspL2045',
  'tspL2050',
  'tspL2055',
  'tspL2060',
  'tspL2065',
  'tspL2070',
  'tspL2075',
]

const StyledTileDiv = styled.div`
  text-align: center;
  position: relative;
  padding: 16px;
  height: 100%;
  cursor: pointer;
  border: 2px solid #f1f1f1;
  background: ${(props) => (props.active ? '#0671ad' : 'transparent')};
  color: ${(props) =>
    props.active ? '#fff !important' : '#344767 !important'};
  border-radius: 0.75rem;
  transition: 0.2s transform ease-in-out;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  &:hover {
    transition: 0.2s all ease-in-out;
    border: 2px solid #a3a7aa;
    transform: scale(1.02);
    background: ${(props) => (props.active ? '#0671ad' : '#f9f9f9')};
    color: ${(props) => (props.active ? '#fff' : '#344767')};
  }

  > h6,
  h5 {
    color: ${(props) => (props.active ? '#fff' : '#344767')};
    letter-spacing: 0.3px;
    font-weight: 500;
    line-height: 1.4;
    margin-top: 8px;
  }
`

export const TSPPageFundSelect = ({ tspAllocation, setTspAllocation }) => (
  <Box p={2}>
    <Typography variant="h4" sx={{ mb: 2 }}>
      Which Funds Are You Invested In?
    </Typography>
    <Typography variant="body2" sx={{ mb: 2 }}>
      Select all that apply:
    </Typography>
    <Grid container spacing={1} columns={6}>
      {FUND_KEYS.map((key) => {
        const data = tspAllocation[key]
        if (!data) return null
        return (
          <Grid item xs={2} key={key}>
            <StyledTileDiv
              active={data.selected}
              onClick={() => {
                setTspAllocation((prev) => {
                  const isLFundButton = data.label === 'L Funds'
                  if (isLFundButton && prev[key].selected) {
                    // deselect all L funds
                    return Object.fromEntries(
                      Object.entries(prev).map(([fk, fd]) => [
                        fk,
                        {
                          ...fd,
                          selected: fd.label.startsWith('L ')
                            ? false
                            : fd.selected,
                          percentage: fd.label.startsWith('L ')
                            ? 0
                            : fd.percentage,
                        },
                      ])
                    )
                  }
                  return {
                    ...prev,
                    [key]: {
                      ...prev[key],
                      selected: !prev[key].selected,
                      percentage: !prev[key].selected
                        ? prev[key].percentage
                        : 0,
                    },
                  }
                })
              }}
            >
              <Typography variant="h5">{data.label}</Typography>
            </StyledTileDiv>
          </Grid>
        )
      })}
    </Grid>
  </Box>
)

// TSPPageLFundSelect.js
import { Checkbox, FormControlLabel } from '@mui/material'

export const TSPPageLFundSelect = ({ tspAllocation, setTspAllocation }) => (
  <div style={{ padding: 20 }}>
    <Typography variant="h4" sx={{ mb: 2 }}>
      Which Specific L Funds Are You Invested In?
    </Typography>
    <Typography variant="body2" sx={{ mb: 2 }}>
      Select all that apply:
    </Typography>
    <Grid container spacing={1} columns={4}>
      {L_FUND_KEYS.map((key) => {
        const data = tspAllocation[key]
        if (!data) return null
        return (
          <Grid item xs={2} key={key}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={!!data.selected}
                  onChange={(e) => {
                    const checked = e.target.checked
                    setTspAllocation((prev) => ({
                      ...prev,
                      [key]: {
                        ...(prev[key] ?? {}),
                        selected: checked,
                        percentage: checked ? prev[key]?.percentage || 0 : 0,
                      },

                      tspLFund: {
                        ...prev.tspLFund,
                        selected:
                          checked ||
                          L_FUND_KEYS.some(
                            (lk) => lk !== key && prev[lk]?.selected
                          ),
                      },
                    }))
                  }}
                />
              }
              label={data.label}
            />
          </Grid>
        )
      })}
    </Grid>
  </div>
)

// TSPPageMixEntry.js
import { renderMuiFormField } from 'utils/forms/renderMuiFormField'

export const TSPPageMixEntry = ({
  tspAllocation,
  setTspAllocation,
  tspTotalMixPercent,
  rawTotal,
}) => (
  <div style={{ padding: 20 }}>
    <Typography variant="h4" sx={{ mb: 2 }}>
      TSP Fund Current Mix
    </Typography>
    <Typography variant="body2" sx={{ mb: 2 }}>
      Fill in how much your current TSP mix is for each fund.
    </Typography>
    <Grid container>
      {FUND_KEYS.concat(L_FUND_KEYS).map((key) => {
        const data = tspAllocation[key]
        if (!data) return null
        if (data.label === 'L Funds') return null
        if (!data.selected) return null
        return (
          <Grid item mt={2} mr={2} xs={12} sm={5.5} key={key}>
            {renderMuiFormField({
              label: data.label,
              inputType: 'percentage',
              value: data.percentage,
              onChange: (value) =>
                setTspAllocation((prev) => ({
                  ...prev,
                  [key]: { ...prev[key], percentage: value },
                })),
            })}
          </Grid>
        )
      })}
    </Grid>
    <Box mt={4} display="flex" alignItems="center">
      <Typography variant="body1">TOTAL:</Typography>
      <Typography
        variant="h3"
        pl={2}
        color={rawTotal !== 100 ? '#FF0000' : '#262627'}
      >
        {rawTotal.toFixed(2)}%
      </Typography>
      <Box>
        <Typography ml={2} variant="body2" color={'#afafaf'}>
          (Must be 100% to continue)
        </Typography>
      </Box>
    </Box>
  </div>
)

export const TSPPageNot100 = ({ tspTotalMixPercent }) => (
  <Box p={2}>
    <Typography variant="h4" sx={{ mb: 2 }}>
      Your total TSP Mix doesn&apos;t equal 100%
    </Typography>
    <Typography variant="body2">
      Try to get your mix percentages as accurate as possible. If you can’t get
      it to 100% we will display the{' '}
      <strong>{tspTotalMixPercent.toFixed(2)}%</strong> you entered for now.
    </Typography>
  </Box>
)

function TSPUnlock({ setFieldErrors, page = 1, fieldErrors }) {
  const { intakeState, updateIntakeStateAtKey } = useContext(IntakeContext)
  const { updateIntakeStateAndSubmit } = useFormDebounce()

  const validators = {
    // Page 2 (fund tiles): must have at least one fund selected
    2: ({ tspAllocation }) => {
      return !Object.values(tspAllocation).some((d) => d.selected)
    },

    // Page 3 (L funds): if "L Funds" is selected, must choose at least one specific L
    3: ({ tspAllocation }) => {
      const hasAnyL = L_FUND_KEYS.some((k) => tspAllocation[k]?.selected)
      return tspAllocation.tspLFund?.selected && !hasAnyL
    },

    // 4: ({ tspAllocation }) => {
    //   return Object.values(tspAllocation).some((d) => {
    //     if (!d.selected) return false
    //     // Invalid if empty string, null, or not a number
    //     return (
    //       d.percentage === '' ||
    //       d.percentage === null ||
    //       isNaN(Number(d.percentage))
    //     )
    //   })
    // },
  }

  const lifecycleFundBreakdowns = {
    tspLIncome: {
      tspGFund: 67.99,
      tspFFund: 5.51,
      tspCFund: 13.79,
      tspSFund: 3.43,
      tspIFund: 9.28,
    },
    tspL2025: {
      tspGFund: 65.1,
      tspFFund: 5.64,
      tspCFund: 15.21,
      tspSFund: 3.81,
      tspIFund: 10.24,
    },
    tspL2030: {
      tspGFund: 34.25,
      tspFFund: 5.63,
      tspCFund: 31.23,
      tspSFund: 7.85,
      tspIFund: 21.04,
    },
    tspL2035: {
      tspGFund: 27.5,
      tspFFund: 6.56,
      tspCFund: 34.23,
      tspSFund: 8.63,
      tspIFund: 23.08,
    },
    tspL2040: {
      tspGFund: 21.29,
      tspFFund: 6.96,
      tspCFund: 37.22,
      tspSFund: 9.42,
      tspIFund: 25.11,
    },
    tspL2045: {
      tspGFund: 15.92,
      tspFFund: 7.33,
      tspCFund: 39.76,
      tspSFund: 10.13,
      tspIFund: 26.86,
    },
    tspL2050: {
      tspGFund: 11.1,
      tspFFund: 7.15,
      tspCFund: 42.33,
      tspSFund: 10.81,
      tspIFund: 28.61,
    },
    tspL2055: {
      tspGFund: 0.54,
      tspFFund: 0.45,
      tspCFund: 51.25,
      tspSFund: 13.11,
      tspIFund: 34.65,
    },
    tspL2060: {
      tspGFund: 0.48,
      tspFFund: 0.51,
      tspCFund: 51.25,
      tspSFund: 13.11,
      tspIFund: 34.65,
    },
    tspL2065: {
      tspGFund: 0.4,
      tspFFund: 0.59,
      tspCFund: 51.25,
      tspSFund: 13.11,
      tspIFund: 34.65,
    },
    tspL2070: {
      tspGFund: 0.36,
      tspFFund: 0.64,
      tspCFund: 51.48,
      tspSFund: 12.87,
      tspIFund: 34.65,
    },
    tspL2075: {
      tspGFund: 0.36,
      tspFFund: 0.64,
      tspCFund: 51.48,
      tspSFund: 12.87,
      tspIFund: 34.65,
    },
  }

  const parsedTspAllocation =
    typeof intakeState.tspAllocation === 'string'
      ? JSON.parse(intakeState.tspAllocation)
      : intakeState.tspAllocation || {}

  const computedRisk = (lFundName, baseRiskMap) => {
    const breakdown = lifecycleFundBreakdowns[lFundName]
    if (!breakdown) return null
    let totalRisk = 0
    Object.entries(breakdown).forEach(([fundName, percent]) => {
      const risk = baseRiskMap[fundName] ?? 0
      totalRisk += (percent / 100) * risk
    })
    return Number(totalRisk.toFixed(2))
  }

  const initialAllocation = useMemo(() => {
    const base = {
      tspGFund: {
        label: 'G Fund',
        percentage: parsedTspAllocation.tspGFund ?? 0,
        selected: !!parsedTspAllocation.tspGFund,
        risk: 1,
        color: '#DD2845',
      },
      tspFFund: {
        label: 'F Fund',
        percentage: parsedTspAllocation.tspFFund ?? 0,
        selected: !!parsedTspAllocation.tspFFund,
        risk: 20,
        color: '#B96EDD',
      },
      tspCFund: {
        label: 'C Fund',
        percentage: parsedTspAllocation.tspCFund ?? 0,
        selected: !!parsedTspAllocation.tspCFund,
        risk: 75,
        color: '#60BE64',
      },
      tspSFund: {
        label: 'S Fund',
        percentage: parsedTspAllocation.tspSFund ?? 0,
        selected: !!parsedTspAllocation.tspSFund,
        risk: 99,
        color: '#FD9446',
      },
      tspIFund: {
        label: 'I Fund',
        percentage: parsedTspAllocation.tspIFund ?? 0,
        selected: !!parsedTspAllocation.tspIFund,
        risk: 86,
        color: '#2370ED',
      },
    }

    const baseRiskMap = Object.fromEntries(
      Object.entries(base).map(([key, val]) => [key, val.risk])
    )

    const lFunds = [
      'tspLIncome',
      'tspL2025',
      'tspL2030',
      'tspL2035',
      'tspL2040',
      'tspL2045',
      'tspL2050',
      'tspL2055',
      'tspL2060',
      'tspL2065',
      'tspL2070',
      'tspL2075',
    ]

    base['tspLFund'] = {
      label: 'L Funds',
      selected: lFunds.some((key) => Number(parsedTspAllocation[key] ?? 0) > 0),
      color: '#344767',
    }

    lFunds.forEach((key) => {
      base[key] = {
        label: key.replace('tspL', 'L ').trim(),
        percentage: parsedTspAllocation[key] ?? 0, // force number
        selected: !!parsedTspAllocation[key], // default false
        color: '#344767',
        risk: computedRisk(key, baseRiskMap),
      }
    })

    return base
  }, [parsedTspAllocation])

  const isValidDetailsShape = (obj) =>
    obj &&
    typeof obj === 'object' &&
    Object.keys(obj).length > 0 &&
    typeof Object.values(obj)[0] === 'object' &&
    'label' in Object.values(obj)[0] &&
    'percentage' in Object.values(obj)[0]

  const [tspAllocation, setTspAllocation] = useState(() => {
    if (isValidDetailsShape(intakeState.tspDetails)) {
      return intakeState.tspDetails
    }
    return initialAllocation
  })

  const [tspTotalMixPercent, setTspTotalMixPercent] = useState(0)

  function normalizeAllocation(allocation) {
    const total = Object.values(allocation).reduce(
      (sum, d) => sum + (parseFloat(d.percentage) || 0),
      0
    )

    // allow wiggle for floating point
    if (total === 0) return allocation
    if (total > 99.99 && total < 100.01) return allocation // already ~100%

    // scale everything proportionally
    const scale = 100 / total
    const newAlloc = { ...allocation }

    Object.keys(newAlloc).forEach((k) => {
      if (k === 'tspLFund') return
      const pct = parseFloat(newAlloc[k].percentage) || 0
      newAlloc[k] = {
        ...newAlloc[k],
        percentage: pct * scale,
      }
    })

    return newAlloc
  }

  const rawTotal = Object.values(tspAllocation).reduce(
    (sum, d) => sum + (parseFloat(d.percentage) || 0),
    0
  )

  useEffect(() => {
    const adjusted = normalizeAllocation(tspAllocation)

    const parsedData = Object.fromEntries(
      Object.entries(adjusted).map(([key, data]) => [
        key,
        parseFloat(data.percentage) || 0,
      ])
    )

    updateIntakeStateAtKey({ tspAllocation: JSON.stringify(parsedData) })
    updateIntakeStateAtKey({ tspDetails: adjusted })

    setTspTotalMixPercent(
      Object.values(parsedData).reduce((sum, v) => sum + v, 0)
    )
  }, [tspAllocation])

  const invalid =
    validators[page]?.({ tspAllocation, tspTotalMixPercent }) ?? false
  useEffect(() => {
    if (typeof setFieldErrors === 'function') {
      setFieldErrors((prev) => ({ ...prev, tsp: invalid }))
    }
  }, [invalid, setFieldErrors])

  if (page === 1) {
    return (
      <div style={{ filter: 'grayscale(90%)' }}>
        <NewTsp isDemo={true} />
      </div>
    )
  }

  const TSPPageUnlocked = ({ tspAllocation }) => {
    return (
      <Box p={2} sx={{ textAlign: 'left' }}>
        <Typography variant="h4" sx={{ mb: 2 }}>
          TSP Breakdown tile unlocked!
        </Typography>
        <Typography variant="body2" sx={{ mb: 2 }}>
          You can now see how your TSP is allocated and how much is in each
          fund, as well as specific fund details.
        </Typography>

        <Box sx={{ mt: 3 }}>
          <NewTsp
            isDemo={true}
            isUnlocked={true}
            tspAllocation={tspAllocation}
          />
        </Box>
      </Box>
    )
  }

  const PAGE_COMPONENTS = {
    1: TSPPageIntro,
    2: TSPPageFundSelect,
    3: TSPPageLFundSelect,
    4: TSPPageMixEntry,
    5: TSPPageNot100,
    6: TSPPageUnlocked,
  }

  const PageComponent = PAGE_COMPONENTS[page]

  return (
    <Box sx={{ textAlign: 'left' }}>
      {PageComponent && (
        <PageComponent
          tspAllocation={tspAllocation}
          setTspAllocation={setTspAllocation}
          tspTotalMixPercent={tspTotalMixPercent}
          rawTotal={rawTotal}
        />
      )}
    </Box>
  )
}

TSPUnlock.isInvalid = ({ fieldErrors }) => !!fieldErrors.tsp

export default TSPUnlock
